import {useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {useQueryClient} from 'react-query';
import {Button, DataTable, IconButton, Title} from 'react-native-paper';
import {useAlerts} from 'react-native-paper-alerts';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import firestore from '@react-native-firebase/firestore';

import {
  CreditTransaction,
  CreditTransactionType,
  CreditType,
  LimitedPartner,
  Subscriber,
  creditExchangeTransactionType,
  useCreditTransactions,
  useCreditTypes,
  useFunds,
  useReservationYears,
  useSubscribers,
} from './../hooks/useDomainData';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';
import {useAppSettings} from '../components/AppSettings';
import {CreditTransactionEditor} from '../components/FormikStoreEditors/CreditTransactionEditor';

type AnnualCreditsAllocationProps = {
  resYear: string;
};
const AnnualCreditsAllocation = (props: AnnualCreditsAllocationProps) => {
  const person = useAuthPersonInfo();
  const subscribers = useSubscribers();
  const creditTransactions = useCreditTransactions();
  const creditTypes = useCreditTypes();
  const funds = useFunds();
  const reservationYears = useReservationYears();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();
  const Alert = useAlerts();
  const [txnDialogVisible, setTxnDialogVisible] = useState(false);
  const [currentCreditTransaction, setCurrentCreditTransaction] = useState<
    CreditTransaction | undefined
  >();

  if (!person || (!person.isAdmin && !person.isGuestRelations)) {
    return null;
  }

  const loading =
    !creditTransactions ||
    !subscribers ||
    !creditTypes ||
    !funds ||
    !reservationYears;

  // Gather LimitedPartners in Funds accepting reservations,
  // but remember what Subscriber they belong to so we may access Subscriber info
  const limitedPartners: LimitedPartner[] = [];
  const limitedPartnerRefIdSubscriberMap: {[key: string]: Subscriber} = {};
  subscribers?.forEach(sub => {
    if (sub.fund?.acceptingReservations !== true) {
      return;
    }

    if (sub.subscriberDivestmentDate !== undefined) {
      return;
    }

    sub.limitedPartners?.forEach(lp => {
      limitedPartners.push(lp);
      limitedPartnerRefIdSubscriberMap[lp.ref.id] = sub;
    });
  });
  limitedPartners.sort((a, b) =>
    a.limitedPartnerName.localeCompare(b.limitedPartnerName),
  );

  // Get the reservation year we are currently inside
  // TODO dropdown to change allocation year maybe? With default to this year?
  const reservationYear = reservationYears?.find(
    year => year.name === props.resYear,
  );
  console.log(
    // `we are in reservation year ${JSON.stringify(reservationYear, null, 2)}`,
    `we are in reservation year ${reservationYear?.name} / ${reservationYear?.reservationYearId}`,
  );

  // We need the credit types to come sorted by expiration time, with the
  // latest times first, to handle overlapping reservation years
  creditTypes?.sort((a: CreditType, b: CreditType) => {
    if (a.expirationYear === undefined && b.expirationYear === undefined) {
      return 0;
    }
    if (a.expirationYear === undefined) {
      return -1;
    }
    if (b.expirationYear === undefined) {
      return 1;
    }
    if (a.expirationYear < b.expirationYear) {
      return 1;
    }
    if (a.expirationYear > b.expirationYear) {
      return -1;
    }
    return 0;
  });
  // creditTypes?.forEach(creditType => {
  //   console.log(
  //     `creditType ${creditType.creditType} w/expiration ${creditType.expirationYear}`,
  //   );
  // });

  // Gather the CreditTypes for this year, for Funds accepting reservations
  const fundRefIdPVCreditTypeMap: {[key: string]: CreditType} = {};
  funds?.forEach(fund => {
    if (!fund.acceptingReservations) {
      return;
    }
    const creditType = creditTypes?.find(
      type =>
        type.fund?.ref.id === fund.ref.id &&
        type.expirationYear !== undefined &&
        reservationYear !== undefined &&
        type.expirationYear > reservationYear?.startDate &&
        type.expirationYear <= reservationYear?.endDate,
    );
    if (creditType === undefined) {
      console.log(
        `No ${props.resYear} CreditType found for fund ${fund.fundCode}`,
      );
      return;
    } else {
      console.log(
        `Found CreditType ${creditType.creditType} for ${fund.fundCode} in ${props.resYear} - ${creditType.ref.id}`,
      );
    }
    fundRefIdPVCreditTypeMap[fund.ref.id] = creditType;
  });

  function calculateUnits(lp: LimitedPartner): number {
    return (
      lp.limitedPartnerUnits ??
      Number.parseFloat(
        limitedPartnerRefIdSubscriberMap[lp.ref.id].subscriberUnits,
      ) / limitedPartnerRefIdSubscriberMap[lp.ref.id].limitedPartnerIds!.length
    );
  }

  function calculateCreditsForUnits(lp: LimitedPartner, units: number): number {
    // EPF and EPF2 are both 120 credits for first unit
    if (units <= 1) {
      return 120 * units;
    }

    const subscriber = limitedPartnerRefIdSubscriberMap[lp.ref.id];
    if (subscriber.fund?.fundCode === 'EPF') {
      return 120 + 160 * (units - 1);
    }
    if (subscriber.fund?.fundCode === 'EPF2') {
      return 120 + 180 * (units - 1);
    }

    return -1;
  }

  const handleDeleteTransaction = async (
    txn: CreditTransaction,
    invalidate: boolean,
  ) => {
    console.log(
      `delete ${txn.transactionType} txn for ${txn.limitedPartner?.limitedPartnerName}`,
    );
    await firestore().doc(txn.ref.path).delete();
    if (invalidate) {
      queryClient.invalidateQueries('creditTransactions');
    }
  };

  // External funds (EA, TH, Hideaways, ...) can receive exchange credits.
  const exchangeFunds = (funds ?? [])
    .filter(fund => !fund.internal)
    .sort((a, b) => a.fundCode.localeCompare(b.fundCode));

  // Gather all this year's managed CreditTransactions for our LPs
  const lpRefIdCreditTransactionsMap: {
    [lpRefId: string]: {[transactionType: string]: CreditTransaction};
  } = {};
  creditTransactions?.forEach(txn => {
    // If the CreditTransaction isn't automatically managed (here), skip
    if (!txn.managedAutomatically) {
      return;
    }
    // If CreditType is for a Fund that isn't accepting reservations, skip
    if (!txn.creditType?.fund?.acceptingReservations) {
      return;
    }
    // If the transaction isn't for one of our year's credit types, skip it
    let txnCreditTypeIsInResYear = false;
    Object.keys(fundRefIdPVCreditTypeMap).forEach(fundRefId => {
      if (fundRefIdPVCreditTypeMap[fundRefId].ref.id === txn.creditTypeId.id) {
        txnCreditTypeIsInResYear = true;
      }
    });
    if (!txnCreditTypeIsInResYear) {
      return;
    }

    // This is definitely a CreditTransaction we are managing this year. File it.
    let lpTxnMap = lpRefIdCreditTransactionsMap[txn.limitedPartnerId.id];
    if (lpTxnMap === undefined) {
      lpTxnMap = {};
      lpRefIdCreditTransactionsMap[txn.limitedPartnerId.id] = lpTxnMap;
    }
    if (
      txn.transactionType === CreditTransactionType.AnnualAllocation &&
      lpTxnMap[txn.transactionType] !== undefined
    ) {
      console.log(
        `WARN duplicate allocate txn for ${txn.limitedPartnerId.path}`,
      );
      handleDeleteTransaction(txn, true);
      return;
    }
    lpTxnMap[txn.transactionType] = txn;
  });

  function calculateCreditsForTransactionType(
    lp: LimitedPartner,
    type: string,
  ): number {
    const existingTxn = lpRefIdCreditTransactionsMap[lp.ref.id]?.[type];
    if (existingTxn !== undefined) {
      return parseInt(existingTxn.credits, 10);
    }
    if (type === CreditTransactionType.AnnualAllocation) {
      return calculateCreditsForUnits(lp, calculateUnits(lp));
    }
    return 0;
  }

  function getTxnForTransactionType(
    lp: LimitedPartner,
    type: string,
  ): CreditTransaction | undefined {
    return lpRefIdCreditTransactionsMap[lp.ref.id]?.[type];
  }

  const handleSaveAllAnnualAllocations = async () => {
    console.log('Saving annual allocation credit transactions');
    // for each LP, create+save txn w/default credits if none exists
    for (const lp of limitedPartners) {
      let txn = getTxnForTransactionType(
        lp,
        CreditTransactionType.AnnualAllocation,
      );
      if (txn !== undefined) {
        // if the lp already has a txn, no need to create a new one
        // since they save immediately when edited directly
        continue;

        // ...or if you comment out the continue above and uncomment this,
        // you can re-create all of them
        // await handleDeleteTransaction(txn, false);
      }
      console.log(`  creating annual txn for ${lp.limitedPartnerName}`);
      txn = createCreditTransaction(lp, CreditTransactionType.AnnualAllocation);
      await firestore().collection('creditTransactions').add(txn);
    }

    queryClient.invalidateQueries('creditTransactions');
    console.log('Done saving annual allocation credit transactions');
  };

  const handleDeleteTransactions = async (lp: LimitedPartner) => {
    console.log(`deleting all annual txns for ${lp.limitedPartnerName}`);
    const lpTxnMap = lpRefIdCreditTransactionsMap[lp.ref.id];
    for (const txn of Object.values(lpTxnMap)) {
      await handleDeleteTransaction(txn, false);
    }
    queryClient.invalidateQueries('creditTransactions');
  };

  const createCreditTransaction = (lp: LimitedPartner, type: string) => {
    return {
      allocationDate: reservationYear?.startDate,
      expirationDate:
        type === CreditTransactionType.AnnualAllocation
          ? reservationYear?.endDate
          : undefined,
      creditTransactionId: Date.now(),
      credits: calculateCreditsForTransactionType(lp, type) + '',
      limitedPartnerId: lp.ref,
      transactionType: type,
      creditTypeId:
        fundRefIdPVCreditTypeMap[
          limitedPartnerRefIdSubscriberMap[lp.ref.id].fundId.id
        ].ref,
      managedAutomatically: true,
    } as CreditTransaction;
  };

  const handleCreateEditCreditTransaction = (
    lp: LimitedPartner,
    type: string,
  ) => {
    try {
      let creditTransaction = getTxnForTransactionType(lp, type);
      if (creditTransaction === undefined) {
        creditTransaction = createCreditTransaction(lp, type);
      }

      // console.log('should edit credit transactionPath: ' + creditTransaction);
      setCurrentCreditTransaction(creditTransaction);
      setTxnDialogVisible(true);
    } catch (error) {
      console.error('Unable to create or edit credit transaction?', error);
      Alert.alert(
        'Error',
        'There was an error while saving, are the credit types for this year defined? ' +
          error,
        [{text: appSettings.t('OK')}],
      );
    }
  };

  function renderCreditsCellForType(lp: LimitedPartner, type: string) {
    return (
      <>
        {calculateCreditsForTransactionType(lp, type)}
        <IconButton
          icon="content-save-edit-outline"
          style={styles.noRightSpacing}
          size={18}
          color={appSettings.currentTheme.colors.primary}
          onPress={() => {
            console.log(
              `save/edit txn for ${lp.limitedPartnerId}, type ${type}`,
            );
            handleCreateEditCreditTransaction(lp, type);
          }}
        />
        {getTxnForTransactionType(lp, type) !== undefined && (
          <IconButton
            icon="delete-outline"
            style={styles.noLeftSpacing}
            size={18}
            color={appSettings.currentTheme.colors.primary}
            onPress={async () =>
              await handleDeleteTransaction(
                getTxnForTransactionType(lp, type)!,
                true,
              )
            }
          />
        )}
        {getTxnForTransactionType(lp, type) === undefined && (
          <Icon
            name="blank"
            style={styles.noLeftSpacing}
            size={18}
            color={appSettings.currentTheme.colors.primary}
          />
        )}
      </>
    );
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>
          Annual Credit Allocations - {props.resYear}
        </Title>
        <Button
          mode="outlined"
          onPress={() => handleSaveAllAnnualAllocations()}>
          Save Annual Allocations
        </Button>
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {loading && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {!loading && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.widerCell}>
                LP Name
              </DataTable.Title>
              <DataTable.Title style={styles.borderCell}>Fund</DataTable.Title>
              <DataTable.Title style={styles.borderCell}>Units</DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                Annual Credits
              </DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                Fee Offset?
              </DataTable.Title>
              {exchangeFunds.map(fund => (
                <DataTable.Title key={fund.ref.id} style={styles.borderCell}>
                  {fund.fundCode}?
                </DataTable.Title>
              ))}
              <DataTable.Title style={styles.borderCell}>
                Actions
              </DataTable.Title>
            </DataTable.Header>
          )}
          {!loading && limitedPartners.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No limited partners found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {!loading &&
            limitedPartners.length > 0 &&
            limitedPartners.map(lp => (
              <DataTable.Row key={lp.limitedPartnerId}>
                <DataTable.Cell style={styles.widerCell}>
                  {lp.limitedPartnerName}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {limitedPartnerRefIdSubscriberMap[lp.ref.id].fund?.fundCode}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {calculateUnits(lp)}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {renderCreditsCellForType(
                    lp,
                    CreditTransactionType.AnnualAllocation,
                  )}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {renderCreditsCellForType(
                    lp,
                    CreditTransactionType.AnnualFeeOffset,
                  )}
                </DataTable.Cell>
                {exchangeFunds.map(fund => (
                  <DataTable.Cell key={fund.ref.id} style={styles.borderCell}>
                    {renderCreditsCellForType(
                      lp,
                      creditExchangeTransactionType(fund.fundCode),
                    )}
                  </DataTable.Cell>
                ))}
                <DataTable.Cell style={styles.borderCell}>
                  <IconButton
                    icon="delete-outline"
                    style={styles.noLeftSpacing}
                    size={18}
                    color={appSettings.currentTheme.colors.primary}
                    onPress={async () => await handleDeleteTransactions(lp)}
                  />
                </DataTable.Cell>
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>
      <CreditTransactionEditor
        allLimitedPartners={limitedPartners ?? []}
        filteredReservations={[] /* FIXME need to fetch reservations */}
        dialogVisible={txnDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setTxnDialogVisible(visible);
          if (!visible) {
            setCurrentCreditTransaction(undefined);
          }
        }}
        creditTransaction={currentCreditTransaction}
      />
    </>
  );
};

const styles = StyleSheet.create({
  widerCell: {
    minWidth: 150,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
  noRightSpacing: {paddingRight: 0, marginRight: 0},
  noLeftSpacing: {paddingLeft: 0, marginLeft: 0},
});

export default AnnualCreditsAllocation;
